import { Input, Loader, LogService, Selector, SpiderNoirService } from '@hub/shared'
import { Profile } from '@hub/shared/interfaces/mediakit.interface'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import './brand-select.component.scoped.scss'
import './brand-select.component.scss'

export interface ProfileFound {
  id: string
  username: string
  profile_picture: string
  full_name: string
}

interface Validation {
  brand: boolean
}

interface Props {
  onSelectProfile?: (profile: Profile) => void
}

export default ({ onSelectProfile }: Props) => {
  const spiderNoirService = useMemo(() => new SpiderNoirService(), [])
  const logService = useMemo(() => new LogService(), [])
  logService.init('BrandSelect Component')

  const [timeouted, setTimeouted] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [brand, setBrand] = useState<string>('')
  const [listProfilesFound, setListProfilesFound] = useState<ProfileFound[]>([])
  const [listProfilesEmpty, setListProfilesEmpty] = useState<boolean>(false)
  const [brandSelected, setBrandSelected] = useState<string | null>(null)

  const [t] = useTranslation('brandSelect')

  const handleChange = (brand: string) => {
    setBrand(brand)
  }

  const handleSelectProfile = (profile: ProfileFound) => {
    if (onSelectProfile && typeof onSelectProfile === 'function') {
      onSelectProfile({ id: profile.id, picture: profile.profile_picture, username: profile.username })
    }
    setBrandSelected(profile?.id)
  }

  useEffect(() => {
    const action = async () => {
      if (timeouted) {
        try {
          setLoading(true)
          const listProfilesFound = await spiderNoirService.getProfiles(brand.startsWith('@') ? brand.substring(1) : brand)
          if (listProfilesFound.length > 0) {
            setListProfilesFound(listProfilesFound)
          } else {
            setListProfilesEmpty(true)
          }
        } catch (error) {
          setListProfilesFound([])
          logService.error({
            message: 'Failed to get profiles',
            error,
          })
        } finally {
          setLoading(false)
        }
      }
    }

    action()
  }, [brand, spiderNoirService, logService, timeouted])

  const onTimeout = (timeouted: boolean) => {
    setTimeouted(timeouted)
  }

  return (
    <div className="brand-select">
      <form className="brand-select-form" onSubmit={(e) => e.preventDefault()}>
        <Input
          placeholder={t('searchProfile')}
          value={brand}
          name="brand-select"
          id="brand-select"
          className="brand-select-field"
          onChange={handleChange}
          onTimeout={onTimeout}
          externalIcon={<i className="fa-regular fa-magnifying-glass"></i>}
          autoFocus
        />
      </form>
      <div className={`brand-list-found scrollbar ${loading && 'p-2'}`}>
        {loading ? (
          <Loader />
        ) : listProfilesFound?.length > 0 ? (
          listProfilesFound?.map((profile, index) => (
            <div className="brand-found-item" role="button" tabIndex={index} key={profile?.id} onClick={() => handleSelectProfile(profile)}>
              <div className="brand-image-found">
                <figure>
                  <img src={profile?.profile_picture} alt={profile?.username} />
                </figure>
              </div>
              <div className="brand-description-found">
                <span className="brand-fullname">{profile?.full_name}</span>
                <span className="brand-username">{`@${profile?.username}`}</span>
              </div>
              <Selector
                name={`select-brand-${index}`}
                id={`select-brand-${index}`}
                errorSpan={false}
                className="selector-brand"
                value={profile}
                checked={brandSelected === profile?.id}
                type="radio"
              />
            </div>
          ))
        ) : listProfilesEmpty ? (
          <div
            className="list-empty"
            dangerouslySetInnerHTML={{
              __html: t('profileNotFound'),
            }}
          ></div>
        ) : null}
      </div>
    </div>
  )
}
